import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type { Seat } from "@repo/ui/types/seats";

import { useAppSelector } from "../hooks";

// Type for passenger field values
type PassengerFieldValue = string | number | boolean | object | null | undefined;

export type PassengerType = "Adulto" | "Niño" | "Infante" | "ADT" | "CHD" | "INF";

export type WheelchairType =
  | "wheelchair___cannot_walk"
  | "wheelchair___cannot_climb_stairs"
  | "wheelchair___can_climb_stairs"
  | null;

// CMS-driven special assistance option (codename + display label).
// Stored here so the lateral breakdown can render the exact checkbox label.
export interface SpecialAssistanceOption {
  codename: string;
  value: string;
  name?: string;
}

export const OXYGEN_ASSISTANCE_CODENAME = "portable_oxygen_concentrator";

export interface PassengerAddress {
  status?: number;
  companyName?: string | null;
  lineOne?: string;
  passengerAddressKey?: string;
  phone?: string;
  lineTwo?: string | null;
  stationCode?: string | null;
  lineThree?: string | null;
  emailAddress?: string;
  countryCode?: string | null;
  cultureCode?: string;
  provinceState?: string | null;
  refusedContact?: boolean;
  city?: string | null;
  postalCode?: string | null;
}

export interface Passenger {
  id: number;
  key?: string; // Passenger key from API
  passengerType: PassengerType;
  name?: string;
  firstName: string;
  lastName: string;
  title?: string;
  hasInfant?: boolean; // Whether passenger has an associated infant
  dob: { day: string; month: string; year: string };
  aadvantage: string;
  aadvantageOverride?: boolean;
  documentNumber?: string;
  documentType?: string;
  type?: PassengerType;
  address?: string;
  email?: string;
  phone?: string;
  callingCode?: string;
  residenceCountry?: string;
  gender?: string;
  addresses?: PassengerAddress[];
  discountCode?: string;
  // Infant information (if passenger has an infant)
  infant?: {
    name: string;
    dateOfBirth: { day: string; month: string; year: string };
    gender: string;
    nationality: string;
    residentCountry: string;
    documentType: string;
    documentNumber: string;
    documentExpirationDate: { day: string; month: string; year: string };
    documentIssueDate: { day: string; month: string; year: string };
    documentIssuingCountry: string;
  };

  // Additional information object containing all additional fields
  additionalInformation?: {
    // Universal fields
    nationality?: string;
    documentIssueDate?: { day: string; month: string; year: string };
    // Colombia-specific fields
    documentIssuingCountry?: string;
    documentExpirationDate?: { day: string; month: string; year: string };
    arrivalZipCode?: string;
    arrivalCity?: string;
    arrivalDestination?: string;
    // Peru-specific fields
    country?: string;
    city?: string;
    zipCode?: string;
  };

  assistance: {
    wheelchair: WheelchairType;
    oxygen: boolean;
  };
  countries: {
    departureCountry?: string;
    destinationCountry?: string;
  };
  seat?: Seat | null;
}

/** Staff booking: whether the staff member is one of the passengers ("¿Viajarás?"). */
export type StaffTravelStatus = "travelling" | "not-travelling";

export interface PassengerState {
  passengers: Passenger[];
  confirmed: boolean;
  // CMS special assistance options, used to render breakdown labels.
  specialAssistanceOptions: SpecialAssistanceOption[];
  isPassengerStepValid: boolean;
  // Persisted with the passengers so a page refresh keeps the staff member's answer.
  staffTravelStatus: StaffTravelStatus;
}

export type PassengerDefaultValues = Partial<Pick<Passenger, "firstName" | "lastName">>;

// Validation function to check if a passenger has all mandatory fields
const validatePassenger = (passenger: Passenger): boolean => {
  const hasName = passenger.firstName.trim() !== "" && passenger.lastName.trim() !== "";
  const hasDOB =
    passenger.dob.day.trim() !== "" &&
    passenger.dob.month.trim() !== "" &&
    passenger.dob.year.trim() !== "";

  // For adults, only name is mandatory. For children and infants, both name and DOB are mandatory
  if (passenger.passengerType === "Adulto") {
    return hasName;
  } else {
    return hasName && hasDOB;
  }
};

// Validation function to check if all passengers are valid
const validateAllPassengers = (passengers: Passenger[]): boolean => {
  return passengers.length > 0 && passengers.every(validatePassenger);
};

const makePassenger = (
  id: number,
  passengerType: PassengerType,
  defaultValues?: PassengerDefaultValues,
): Passenger => ({
  id,
  passengerType,
  firstName: defaultValues?.firstName ?? "",
  lastName: defaultValues?.lastName ?? "",
  dob: { day: "", month: "", year: "" },
  aadvantage: "",
  documentNumber: "",
  documentType: "",
  addresses: [],
  email: "",
  phone: "",
  residenceCountry: "",
  gender: "",
  discountCode: "",
  // Additional information object
  additionalInformation: {
    // Universal fields
    nationality: "",
    documentIssueDate: { day: "", month: "", year: "" },
    // Colombia-specific fields
    documentIssuingCountry: "",
    documentExpirationDate: { day: "", month: "", year: "" },
    arrivalZipCode: "",
    arrivalCity: "",
    arrivalDestination: "",
    // Peru-specific fields
    country: "",
    city: "",
    zipCode: "",
  },

  assistance: { wheelchair: null, oxygen: false },
  countries: { departureCountry: "", destinationCountry: "" },
});

const initialState: PassengerState = {
  passengers: [],
  confirmed: false,
  specialAssistanceOptions: [],
  isPassengerStepValid: true,
  staffTravelStatus: "travelling",
};

const passengerSlice = createSlice({
  name: "passenger",
  initialState,
  reducers: {
    initializePassengers: (
      state,
      action: PayloadAction<{
        adults: number;
        children: number;
        infants?: number;
        defaultValues?: {
          adults?: Array<PassengerDefaultValues | undefined>;
        };
      }>,
    ) => {
      const { adults, children, infants = 0, defaultValues } = action.payload;
      const passengers: Passenger[] = [];
      let passengerId = 1;

      // Add adults
      for (let i = 1; i <= adults; i++) {
        const passenger = makePassenger(passengerId++, "Adulto", defaultValues?.adults?.[i - 1]);
        passengers.push(passenger);
      }

      // Add children
      for (let i = 1; i <= children; i++) {
        passengers.push(makePassenger(passengerId++, "Niño"));
      }

      // Add infants
      for (let i = 1; i <= infants; i++) {
        passengers.push(makePassenger(passengerId++, "Infante"));
      }

      state.passengers = passengers;
      state.confirmed = false; // Reset confirmation when initializing new passengers
    },
    updatePassenger: (state, action: PayloadAction<{ index: number; passenger: Passenger }>) => {
      const { index, passenger } = action.payload;
      if (index >= 0 && index < state.passengers.length) {
        state.passengers[index] = passenger;
      }
    },
    setPassengersCountries: (state, action: PayloadAction<Passenger["countries"]>) => {
      const countries = action.payload;
      state.passengers.forEach((passenger) => {
        if (
          passenger.countries?.departureCountry !== countries.departureCountry ||
          passenger.countries?.destinationCountry !== countries.destinationCountry
        ) {
          passenger.countries = countries;
        }
      });
    },
    setPassengersDiscountCode: (state, action: PayloadAction<string>) => {
      state.passengers.forEach((passenger) => {
        if (passenger.discountCode !== action.payload) {
          passenger.discountCode = action.payload;
        }
      });
    },
    setMissingPassengerDocumentType: (state, action: PayloadAction<string>) => {
      state.passengers.forEach((passenger) => {
        if (!passenger.documentType) {
          passenger.documentType = action.payload;
        }
      });
    },
    setMissingPassengerResidenceCountry: (state, action: PayloadAction<string>) => {
      state.passengers.forEach((passenger) => {
        if (
          passenger.passengerType !== "Niño" &&
          passenger.passengerType !== "Infante" &&
          !passenger.residenceCountry
        ) {
          passenger.residenceCountry = action.payload;
        }
      });
    },
    updatePassengerField: (
      state,
      action: PayloadAction<{
        index: number;
        field: keyof Passenger;
        value: PassengerFieldValue;
        subField?: string;
      }>,
    ) => {
      const { index, field, value, subField } = action.payload;
      if (index >= 0 && index < state.passengers.length) {
        const passenger = state.passengers[index];
        if (passenger) {
          if (subField && typeof passenger[field] === "object" && passenger[field] !== null) {
            (passenger[field] as Record<string, PassengerFieldValue>)[subField] = value;
          } else {
            (passenger as Record<string, PassengerFieldValue>)[field] = value;
          }
        }
      }
    },
    updatePassengerAssistance: (
      state,
      action: PayloadAction<{
        index: number;
        type: "wheelchair" | "oxygen";
        value: WheelchairType | boolean;
      }>,
    ) => {
      const { index, type, value } = action.payload;
      if (index >= 0 && index < state.passengers.length) {
        const passenger = state.passengers[index];
        if (passenger) {
          if (type === "wheelchair") {
            passenger.assistance.wheelchair = value as WheelchairType;
          } else if (type === "oxygen") {
            passenger.assistance.oxygen = value as boolean;
          }
        }
      }
    },
    setSpecialAssistanceOptions: (state, action: PayloadAction<SpecialAssistanceOption[]>) => {
      const options = action.payload ?? [];
      const unchanged =
        state.specialAssistanceOptions.length === options.length &&
        state.specialAssistanceOptions.every(
          (option, index) =>
            option.codename === options[index]?.codename &&
            option.value === options[index]?.value &&
            option.name === options[index]?.name,
        );
      if (!unchanged) state.specialAssistanceOptions = options;
    },
    setIsPassengerStepValid: (state, action: PayloadAction<boolean>) => {
      if (state.isPassengerStepValid !== action.payload) {
        state.isPassengerStepValid = action.payload;
      }
    },
    clearPassengers: (state) => {
      state.passengers = [];
      state.staffTravelStatus = initialState.staffTravelStatus;
    },
    setStaffTravelStatus: (state, action: PayloadAction<StaffTravelStatus>) => {
      state.staffTravelStatus = action.payload;
    },
    setPassengers: (state, action: PayloadAction<Passenger[]>) => {
      state.passengers = action.payload;
    },
    updatePassengerAndValidate: (
      state,
      action: PayloadAction<{ index: number; passenger: Passenger }>,
    ) => {
      const { index, passenger } = action.payload;
      if (index >= 0 && index < state.passengers.length) {
        state.passengers[index] = passenger;
        // Update confirmation state based on all passengers
        state.confirmed = validateAllPassengers(state.passengers);
      }
    },
    validatePassengers: (state) => {
      state.confirmed = validateAllPassengers(state.passengers);
    },
    confirmPassengers: (state) => {
      state.confirmed = true;
    },
    resetPassengerConfirmation: (state) => {
      state.confirmed = false;
    },
  },
});

// Hooks
export const usePassengerState = () => useAppSelector((state) => state.passenger);
export const usePassengers = () => useAppSelector((state) => state.passenger.passengers);
// State persisted before this field existed rehydrates without it.
export const useStaffTravelStatus = () =>
  useAppSelector((state) => state.passenger.staffTravelStatus ?? initialState.staffTravelStatus);
export const usePassengerConfirmed = () => useAppSelector((state) => state.passenger.confirmed);
export const useSpecialAssistanceOptions = () =>
  useAppSelector((state) => state.passenger.specialAssistanceOptions);
export const useIsPassengerStepValid = () =>
  useAppSelector((state) => state.passenger.isPassengerStepValid);
export const selectIsPassengerStepValid = (s: { passenger: PassengerState }) =>
  s.passenger.isPassengerStepValid;

/**
 * Builds the special-assistance lines for the lateral breakdown's Optionals
 * section. Each selected assistance (per passenger) is represented at $0 using
 * the exact CMS checkbox label. Lines are aggregated per assistance type, with
 * `quantity` reflecting how many passengers have that assistance assigned.
 */
export const getSpecialAssistanceCharges = (
  passengers: Passenger[],
  options: SpecialAssistanceOption[],
): { name: string; quantity: number; amount: number }[] => {
  if (!passengers?.length || !options?.length) return [];

  const countByCodename: Record<string, number> = {};
  passengers.forEach((passenger) => {
    const wheelchair = passenger?.assistance?.wheelchair;
    if (wheelchair) {
      countByCodename[wheelchair] = (countByCodename[wheelchair] ?? 0) + 1;
    }
    if (passenger?.assistance?.oxygen) {
      countByCodename[OXYGEN_ASSISTANCE_CODENAME] =
        (countByCodename[OXYGEN_ASSISTANCE_CODENAME] ?? 0) + 1;
    }
  });

  return options
    .filter((option) => (countByCodename[option.codename] ?? 0) > 0)
    .map((option) => ({
      name: option.value,
      quantity: countByCodename[option.codename] ?? 0,
      amount: 0,
    }));
};

// Utility hook to get a specific passenger
export const usePassenger = (index: number) => {
  const passengers = usePassengers();
  return passengers[index];
};

// Utility hook to get passengers by type
export const usePassengersByType = (type: PassengerType) => {
  const passengers = usePassengers();
  return passengers.filter((p) => p.passengerType === type);
};

// Utility hook to get total passenger count
export const usePassengerCount = () => {
  const passengers = usePassengers();
  return {
    total: passengers.length,
    adults: passengers.filter((p) => p.passengerType === "Adulto").length,
    children: passengers.filter((p) => p.passengerType === "Niño").length,
    infants: passengers.filter((p) => p.passengerType === "Infante").length,
  };
};

export const {
  initializePassengers,
  updatePassenger,
  setPassengersCountries,
  setPassengersDiscountCode,
  setMissingPassengerDocumentType,
  setMissingPassengerResidenceCountry,
  updatePassengerAndValidate,
  // updatePassengerField,
  updatePassengerAssistance,
  setSpecialAssistanceOptions,
  setIsPassengerStepValid,
  clearPassengers,
  setStaffTravelStatus,
  setPassengers,
  validatePassengers,
  confirmPassengers,
  resetPassengerConfirmation,
} = passengerSlice.actions;

export default passengerSlice.reducer;
